import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  Text,
  useBreakpointValue
} from '@chakra-ui/react'
import { AccountCircle, Mic, MicOff } from '@components/icons'
import {
  DailyVideo,
  useAudioLevelObserver,
  useAudioTrack,
  useLocalSessionId,
  useParticipantProperty,
  useVideoTrack
} from '@daily-co/daily-react'
import React, { useEffect, useMemo, useRef } from 'react'

export const ParticipantTile = React.memo(
  ({
    participantId,
    aspectRatio,
    setAspectRatio,
    videoFitOverride,
    isScreenShare = false,
    borderRadius = '16px',
    showTalkingBorder = false
  }: {
    participantId: string
    aspectRatio: number
    setAspectRatio?: (aspectRatio: number) => void
    videoFitOverride?: string
    isScreenShare?: boolean
    borderRadius?: string
    showTalkingBorder?: boolean
  }) => {
    const localSessionId = useLocalSessionId()
    const username = useParticipantProperty(participantId, 'user_name')
    const camTrack = useVideoTrack(participantId)
    // const [isTalking, setIsTalking] = useState(false)
    // const talkingTimeoutRef = useRef<NodeJS.Timeout>()

    // useAudioLevelObserver(
    //   participantId,
    //   useCallback((vol) => {
    //     if (showTalkingBorder) {
    //       if (vol > 0.1) {
    //         setIsTalking(true)
    //         // Clear any existing timeout
    //         if (talkingTimeoutRef.current) {
    //           clearTimeout(talkingTimeoutRef.current)
    //         }
    //         // Set new timeout to turn off talking state after 500ms
    //         talkingTimeoutRef.current = setTimeout(() => {
    //           setIsTalking(false)
    //           }, 500)
    //       }
    //     }
    //   }, [])
    // )

    // Cleanup timeout on unmount
    // useEffect(() => {
    //   return () => {
    //     if (talkingTimeoutRef.current) {
    //       clearTimeout(talkingTimeoutRef.current)
    //     }
    //   }
    // }, [])

    const isLocalTile = participantId === localSessionId

    const displayName = useMemo(() => {
      if (isLocalTile) {
        if (isScreenShare) {
          return 'Your screen'
        } else {
          return 'You'
        }
      } else if (username) {
        if (isScreenShare) {
          return `${username}'s Screen`
        } else {
          return username
        }
      } else {
        if (isScreenShare) {
          return "Clinician's Screen"
        } else {
          return 'Clinician'
        }
      }
    }, [isLocalTile, isScreenShare, username])

    return (
      <Flex
        position="relative"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        // px={showTalkingBorder ? '4px' : '0px'}
      >
        <AspectRatio ratio={aspectRatio} width="100%">
          <Box
            position="relative"
            borderRadius={borderRadius}
            // outline={isTalking ? '3px solid #8198F1' : '1px solid #282828'}
            // outlineOffset={isTalking ? '0px' : '-1px'}
          >
            {isScreenShare ? (
              <MemoizedDailyVideo
                sessionId={participantId}
                isScreenShare={isScreenShare}
              />
            ) : (
              <MemoizedDailyVideo
                sessionId={participantId}
                isScreenShare={isScreenShare}
                setAspectRatio={setAspectRatio}
                isLocalTile={isLocalTile}
                videoFitOverride={videoFitOverride}
              />
            )}
            <InfoOverlay sessionId={participantId} displayName={displayName} />
            {!isScreenShare &&
              (camTrack.isOff || !camTrack.persistentTrack) && (
                <Flex
                  position="absolute"
                  top="0"
                  left="0"
                  width="100%"
                  height="100%"
                  justify="center"
                  align="center"
                  bg="gray.800"
                  borderRadius="12px"
                >
                  <AccountCircle width="80px" fill="#757575" />
                </Flex>
              )}
          </Box>
        </AspectRatio>
      </Flex>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.participantId === nextProps.participantId &&
      prevProps.aspectRatio === nextProps.aspectRatio &&
      prevProps.isScreenShare === nextProps.isScreenShare
    )
  }
)

const InfoOverlay = ({
  displayName,
  sessionId
}: {
  sessionId: string
  displayName: string
}) => {
  const micTrack = useAudioTrack(sessionId)

  const micWidth = useBreakpointValue({
    base: '12px',
    md: '16px',
    lg: '20px'
  })

  return (
    <HStack
      spacing="2px"
      position="absolute"
      bg="rgba(0, 0, 0, 0.4)"
      py={{
        base: '0px',
        sm: '4px'
      }}
      pl={{
        base: '2px',
        sm: '4px'
      }}
      pr={{
        base: '2px',
        sm: '8px'
      }}
      borderRadius="8px"
      bottom={{
        base: '2px',
        sm: '4px',
        md: '4px',
        lg: '8px'
      }}
      left={{
        base: '4px',
        sm: '6px',
        md: '8px',
        lg: '10px'
      }}
      zIndex="5"
    >
      <>
        {micTrack.isOff ? (
          <MicOff width={micWidth} fill="rgba(250, 250, 250, 0.8)" />
        ) : (
          <Mic width={micWidth} fill="rgba(250, 250, 250, 0.8)" />
        )}
        <Text
          color="rgba(250, 250, 250, 0.8)"
          fontSize={{
            base: '8px',
            sm: '10px',
            md: '12px',
            lg: '14px'
          }}
        >
          {displayName}
        </Text>
      </>
    </HStack>
  )
}

const MemoizedDailyVideo = React.memo(
  ({
    sessionId,
    isScreenShare,
    setAspectRatio,
    isLocalTile,
    videoFitOverride
  }: {
    sessionId: string
    isScreenShare: boolean
    setAspectRatio?: (aspectRatio: number) => void
    isLocalTile?: boolean
    videoFitOverride?: string
  }) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const camTrack = useVideoTrack(sessionId)

    useEffect(() => {
      const video = videoRef.current

      if (!video) return

      const observer = new MutationObserver(() => {
        if (video.hasAttribute('controls')) {
          video.removeAttribute('controls')
        }
      })

      observer.observe(video, {
        attributes: true,
        attributeFilter: ['controls']
      })

      return () => observer.disconnect()
    }, [])

    if (isScreenShare) {
      return (
        <DailyVideo
          className="telehealth-video screen-share-video"
          sessionId={sessionId}
          type="screenVideo"
          fit="contain"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1
          }}
        />
      )
    }

    return (
      <DailyVideo
        ref={videoRef}
        className="telehealth-video"
        automirror
        sessionId={sessionId}
        type="video"
        // @ts-ignore
        fit={videoFitOverride || 'cover'}
        onResize={({ width, height }) => {
          if (setAspectRatio) {
            setAspectRatio(width / height)
          }
        }}
        style={{
          zIndex: isLocalTile ? 2 : 1,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          opacity: camTrack.isOff || !camTrack.persistentTrack ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out'
        }}
      />
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.sessionId === nextProps.sessionId &&
      prevProps.isScreenShare === nextProps.isScreenShare
    )
  }
)
